// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import {
    Dealer,
    DealerAutoGroup,
    DealerOemCode,
    DealerProductEnrollments,
    DealerProducts,
    DealerSettings,
    TestDriveSettings,
} from '../interfaces/dealer.interfaces';
import { UserDealers } from '../interfaces/user.interfaces';
import { clone, getEnrollmentStatus } from '../utils/utils';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class DealershipService {
    private _dealers$: BehaviorSubject<UserDealers[]> = new BehaviorSubject([]);
    private _dealer$: BehaviorSubject<Dealer> = new BehaviorSubject(<Dealer>{});
    private _dealerSettings$: BehaviorSubject<DealerSettings[]> = new BehaviorSubject([]);
    dealershipUpdate$: BehaviorSubject<Dealer> = new BehaviorSubject(<Dealer>{});

    dealership = <Dealer>{};
    dealerships = <UserDealers[]>{};
    dealershipSettings = <DealerSettings[]>{};

    constructor(private readonly apiService: ApiService) {}

    get dealershipName(): string {
        return this.dealership.name;
    }

    get dealershipInfo(): Dealer {
        return this.dealership;
    }

    get id(): number {
        return this.dealership.dealerId;
    }

    get queueType(): string {
        return this.dealership.signingPreference;
    }

    get dealers$(): Observable<UserDealers[]> {
        return this._dealers$.asObservable();
    }

    set dealers(dealers: UserDealers[]) {
        this._dealers$.next(clone(dealers));
    }

    get dealer$(): Observable<Dealer> {
        return this._dealer$.asObservable();
    }

    set dealer(dealer: Dealer) {
        this._dealer$.next(dealer);
    }

    get dealerSettings$(): Observable<DealerSettings[]> {
        return this._dealerSettings$.asObservable();
    }

    set dealerSettings(dealerSettings: DealerSettings[]) {
        this._dealerSettings$.next(clone(dealerSettings));
    }

    // Called when logging out the user to prevent any dealers bleeding
    // over into another user's session
    clearDealers() {
        this._dealer$.next(<Dealer>{});
        this._dealers$.next([]);
    }

    getDealer$(dealerId: number, setDealer = true): Observable<Dealer> {
        return this.apiService.getV2$<Dealer>(`api/dealer/${dealerId}`).pipe(
            catchError((error) => {
                // rethrow for additional handling
                throw this.apiService.apiErrorHandler(error);
            }),
            map((response) => {
                if (response?.data) {
                    if (setDealer) {
                        this.dealership = response.data;
                        //update the user dealerId
                        this._dealer$.next(this.dealership);
                        this.dealershipUpdate$.next(this.dealership);
                    }

                    return response.data;
                }
                return null;
            })
        );
    }

    getCurrentDealerAppSettings$(): Observable<DealerSettings[]> {
        return this.apiService.getV2$<DealerSettings[]>('api/dealer/dealerSettings').pipe(
            catchError((error) => {
                // rethrow for additional handling
                throw this.apiService.apiErrorHandler(error);
            }),
            map((response) => {
                if (response?.data) {
                    this.dealershipSettings = response.data;
                    this._dealerSettings$.next(this.dealershipSettings);
                    return response.data;
                }
                return null;
            })
        );
    }

    getDealerAppSettings$(dealerId: number): Observable<DealerSettings[]> {
        return this.apiService.getV2$<DealerSettings[]>(`api/dealer/admin/dealerSettings/${dealerId}`).pipe(
            catchError((error) => {
                // rethrow for additional handling
                throw this.apiService.apiErrorHandler(error);
            }),
            map((response) => {
                if (response?.data) {
                    this.dealershipSettings = response.data;
                    this._dealerSettings$.next(this.dealershipSettings);
                    return response.data;
                }
                return null;
            })
        );
    }

    updateDealerAppSettings$(dealerSettings: DealerSettings[]): Observable<DealerSettings[]> {
        return this.apiService.putV2$<DealerSettings[]>('api/dealer/admin/dealerSettings', dealerSettings).pipe(
            catchError((error) => {
                // rethrow for additional handling
                throw this.apiService.apiErrorHandler(error);
            }),
            map((response) => {
                if (response?.data) {
                    this.dealershipSettings = response.data;
                    this._dealerSettings$.next(this.dealershipSettings);
                    return response.data;
                }
                return null;
            })
        );
    }

    getUserDealers$(): Observable<UserDealers[]> {
        return this.apiService.getV2$<UserDealers[]>('api/user/dealers').pipe(
            map((response) => {
                if (response?.data) {
                    this.dealerships = response.data.map((d) => {
                        d.dealerCodeDisplay = d.oemCode + (d.oemCode && d.oemDealerId ? '-' : '') + d.oemDealerId;
                        return d;
                    });

                    this._dealers$.next(this.dealerships);
                    return response.data;
                }
                return [];
            })
        );
    }

    //This gets active dealers based on produt, user, and activation
    getUserActiveDealers$(product: any): Observable<UserDealers[]> {
        return this.apiService.getV2$<UserDealers[]>(`api/user/dealers/active?productId=${product}`).pipe(
            map((response) => {
                if (response?.data) {
                    this.dealerships = response.data.map((d) => {
                        d.dealerCodeDisplay = d.oemCode + (d.oemCode && d.oemDealerId ? '-' : '') + d.oemDealerId;
                        return d;
                    });

                    this._dealers$.next(this.dealerships);
                    return response.data;
                }
                return [];
            })
        );
    }

    getDealersList$(oem = '', search = '', page = 1, records = 25): Observable<Dealer[]> {
        return this.apiService.getV2$<Dealer[]>(`api/dealer/admin/list?oem=${oem}&search=${search}&page=${page}&records=${records}`).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    getOemDealersList$(oem: string, search = '', page = 1, records = 25): Observable<Dealer[]> {
        return this.apiService.getV2$<Dealer[]>(`api/dealer/admin/oem/list?oem=${oem}&search=${search}&page=${page}&records=${records}`).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    getDealerOemCodeList$(): Observable<DealerOemCode[]> {
        return this.apiService.getV2$<DealerOemCode[]>('api/dealer/admin/oemCodes/list').pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    getAutoGroups$(): Observable<DealerAutoGroup[]> {
        return this.apiService.getV2$<DealerAutoGroup[]>('api/dealer/admin/autoGroups/list').pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    // default config settings
    getDealerConfigSettings$(): Observable<DealerSettings[]> {
        return this.apiService.getV2$<DealerSettings[]>('api/dealer/admin/dealerConfigSettings').pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    getDealerEnrollment$(dealerId: number): Observable<DealerProductEnrollments[]> {
        if (dealerId === undefined) {
            return of([]);
        }

        return this.apiService.getV2$<DealerProductEnrollments[]>(`api/dealer/admin/enrollment/${dealerId}`).pipe(
            catchError((err) => {
                // rethrow for further handling
                throw this.apiService.apiErrorHandler(err);
            }),
            map((response) => {
                if (response?.data) {
                    const enrollments = response.data;

                    const isBaseActive = getEnrollmentStatus(enrollments.find((product) => product.productId === DealerProducts.EzDocsBase));

                    return enrollments.map((product) => {
                        if (product.productId === DealerProducts.EzDocsBase) {
                            product.enableEdit = true;
                        } else if (product.activationDate || product.enrollmentDate || product.terminationDate || product.turnOffAccessDate) {
                            product.enableEdit = true;
                        } else {
                            product.enableEdit = isBaseActive;
                        }

                        product.isActive = getEnrollmentStatus(product);

                        return product;
                    });
                } else {
                    return [];
                }
            })
        );
    }

    adminCreateDealer(dealer: Dealer): Observable<Dealer> {
        return this.apiService.postV3$('api/dealer', dealer).pipe(
            map((response: { data: any; error?; message? }) => {
                if (response) {
                    if (response.error) {
                        return {
                            ...response.error,
                            error: true,
                        };
                    } else if (response.data) {
                        return response.data;
                    }
                }
            })
        );
    }

    updateEnrollmentProduct(dealerId: number, body: any): Observable<Dealer> {
        return this.apiService.putV2$(`api/Dealer/admin/enrollment/${dealerId}/update`, body).pipe(
            map((response: { data: any; error?; message? }) => {
                if (response) {
                    if (response.error) {
                        return {
                            ...response.error,
                            error: true,
                        };
                    } else if (response.data) {
                        return response.data;
                    }
                }
            })
        );
    }

    adminUpdateDealer(dealer: Dealer) {
        return this.apiService.putV2$<Dealer>(`api/dealer/admin/${dealer.dealerId}`, dealer).pipe(
            map((response) => {
                if (response?.error) {
                    return {
                        ...response.error,
                        error: true,
                    };
                }

                return response.data;
            })
        );
    }

    adminUpdateDealerActivator(dealerId: number, dealerActivatorId: number) {
        return this.apiService.putV2$<Dealer>(`api/dealer/admin/${dealerId}/activator`, dealerActivatorId).pipe(
            map((response) => {
                if (response.error) {
                    return {
                        ...response.error,
                        error: true,
                    };
                }

                this.dealership = response.data;
                //update the user dealerId
                this._dealer$.next(this.dealership);
                this.dealershipUpdate$.next(this.dealership);

                return response.data;
            })
        );
    }

    adminUploadDealerLogo(dealerId: number, fileData: FormData) {
        return this.apiService.postV2$(`api/file/${dealerId}`, fileData).pipe(
            catchError((error) => {
                return of(error);
            }),
            map((response: { data: any; error?; message? }) => {
                if (response) {
                    if (response.error) {
                        return {
                            ...response.error,
                            error: true,
                        };
                    } else if (response.data) {
                        return response.data;
                    }
                }
            })
        );
    }

    adminUploadDealerTestDriveBanner(dealerId: number, fileData: FormData) {
        return this.apiService.postV2$(`api/file/testDriveBanner/${dealerId}`, fileData).pipe(
            catchError((error) => {
                return of(error);
            }),
            map((response: { data: any; error?; message? }) => {
                if (response) {
                    if (response.error) {
                        return {
                            ...response.error,
                            error: true,
                        };
                    } else if (response.data) {
                        return response.data;
                    }
                }
            })
        );
    }

    adminGetTestDriveSettings(dealerId: number) {
        return this.apiService.getV2$(`api/dealer/testDriveSettings/${dealerId}`).pipe(
            catchError((error) => {
                return of(error);
            }),
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    adminUpdateTestDriveSettings(dealerId: number, request: TestDriveSettings) {
        return this.apiService.putV2$(`api/dealer/testDriveSettings/${dealerId}`, request).pipe(
            catchError((error) => {
                return of(error);
            }),
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }
}
